import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, auth } from '../firebase';
import ScheduleEditor from '../admin/ScheduleEditor';
import SpecialsEditor from '../admin/SpecialsEditor';
import LocationsEditor from '../admin/LocationsEditor';
import TextAlertsEditor from '../admin/TextAlertsEditor';
import { normalizeLocations, cleanLocations, findSavedByName, newLocationId } from '../admin/locationUtils';

const TABS = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'specials', label: 'Specials' },
  { key: 'locations', label: 'Locations' },
  { key: 'alerts', label: 'Text Alerts' },
];

const LOCATIONS_REF = () => doc(db, 'settings', 'locations');

function Admin() {
  const [user, setUser] = useState(undefined); // undefined = auth loading
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [tab, setTab] = useState(() => {
    const saved = window.sessionStorage.getItem('gcc-admin-tab');
    return TABS.some((t) => t.key === saved) ? saved : 'schedule';
  });
  const [dirtyTabs, setDirtyTabs] = useState({});

  // Saved locations are shared by the Schedule tab (picker) and the Locations tab (editor)
  const [savedLocations, setSavedLocations] = useState(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getDoc(LOCATIONS_REF())
      .then((snap) => {
        if (!cancelled) setSavedLocations(snap.exists() ? normalizeLocations(snap.data()) : []);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setSavedLocations([]);
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    window.sessionStorage.setItem('gcc-admin-tab', tab);
  }, [tab]);

  const anyDirty = Object.values(dirtyTabs).some(Boolean);
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const setDirty = useCallback((key, value) => {
    setDirtyTabs((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);
  const dirtyHandlers = useMemo(
    () => Object.fromEntries(TABS.map((t) => [t.key, (value) => setDirty(t.key, value)])),
    [setDirty]
  );

  const saveLocations = useCallback(async (items) => {
    const clean = cleanLocations(items);
    await setDoc(LOCATIONS_REF(), { items: clean, updatedAt: new Date().toISOString() });
    setSavedLocations(clean);
    return clean;
  }, []);

  // "Save to my locations" from the schedule tab
  const quickSaveLocation = useCallback(async (loc) => {
    const name = (loc.name || '').trim();
    if (!name) return { ok: false, message: 'Type a location name first.' };
    const current = savedLocations || [];
    if (findSavedByName(current, name)) {
      return { ok: true, message: 'Already in your saved locations.' };
    }
    try {
      await saveLocations([...current, { ...loc, id: newLocationId() }]);
      return { ok: true, message: 'Saved to your locations.' };
    } catch (err) {
      console.error(err);
      return { ok: false, message: 'Could not save — try again.' };
    }
  }, [savedLocations, saveLocations]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setLoginError('Login failed. Check your email and password.');
    }
  };

  const handleLogout = () => {
    if (anyDirty && !window.confirm('You have unsaved changes. Log out anyway?')) return;
    signOut(auth);
  };

  if (user === undefined) {
    return (
      <main className="admin-page">
        <div className="schedule-loading"><div className="schedule-spinner" /></div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="admin-page">
        <div className="admin-login-card">
          <h2>EMPLOYEE LOGIN</h2>
          <form onSubmit={handleLogin}>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            {loginError && <p className="admin-error">{loginError}</p>}
            <button type="submit" className="cta-button admin-full-btn">Log In</button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <div className="admin-topbar">
        <h2>GROUNDED ADMIN</h2>
        <button className="admin-link-btn" onClick={handleLogout}>Log out</button>
      </div>

      <div className="admin-tabs" role="tablist" aria-label="Admin sections">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`admin-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {dirtyTabs[t.key] && <span className="admin-tab-dot" title="Unsaved changes" />}
          </button>
        ))}
      </div>

      {/* All tabs stay mounted so switching tabs never loses unsaved edits */}
      <section hidden={tab !== 'schedule'} aria-label="Schedule">
        {savedLocations === null ? (
          <div className="schedule-loading"><div className="schedule-spinner" /></div>
        ) : (
          <ScheduleEditor
            savedLocations={savedLocations}
            onQuickSaveLocation={quickSaveLocation}
            onDirtyChange={dirtyHandlers.schedule}
          />
        )}
      </section>
      <section hidden={tab !== 'specials'} aria-label="Specials">
        <SpecialsEditor onDirtyChange={dirtyHandlers.specials} />
      </section>
      <section hidden={tab !== 'locations'} aria-label="Locations">
        <LocationsEditor
          savedLocations={savedLocations || []}
          loaded={savedLocations !== null}
          onSave={saveLocations}
          onDirtyChange={dirtyHandlers.locations}
        />
      </section>
      <section hidden={tab !== 'alerts'} aria-label="Text alerts">
        <TextAlertsEditor onDirtyChange={dirtyHandlers.alerts} />
      </section>
    </main>
  );
}

export default Admin;
